/**
 * Form Helper Utilities
 *
 * Extracted from GuideSignupFlow.tsx mockup
 * Provides formatting and validation helpers for form inputs
 *
 * Epic: MVP 1.2.1 - Guide Signup Flow
 * Location: src/utils/formHelpers.js
 */

/**
 * Format phone number to (XXX) XXX-XXXX format
 * Automatically formats as user types
 *
 * @param {string} value - Raw phone number input
 * @returns {string} Formatted phone number
 *
 * @example
 * formatPhoneNumber('5551234567') // Returns: '(555) 123-4567'
 * formatPhoneNumber('555') // Returns: '555'
 * formatPhoneNumber('55512') // Returns: '(555) 12'
 */
export const formatPhoneNumber = (value) => {
  // Remove all non-digit characters
  const phoneNumber = value.replace(/\D/g, '');

  // Format based on length
  if (phoneNumber.length < 4) {
    return phoneNumber;
  }

  if (phoneNumber.length < 7) {
    return `(${phoneNumber.slice(0, 3)}) ${phoneNumber.slice(3)}`;
  }

  return `(${phoneNumber.slice(0, 3)}) ${phoneNumber.slice(3, 6)}-${phoneNumber.slice(6, 10)}`;
};

/**
 * Calculate password strength
 * Returns strength level (0-4) and feedback message
 *
 * @param {string} password - Password to evaluate
 * @returns {Object} { strength: number, label: string, color: string, feedback: string }
 *
 * Criteria:
 * - Length >= 8 characters
 * - Contains uppercase letter
 * - Contains lowercase letter
 * - Contains number
 * - Contains special character
 *
 * @example
 * calculatePasswordStrength('password')
 * // Returns: { strength: 1, label: 'Weak', color: 'red', feedback: 'Add uppercase, numbers, and special characters' }
 *
 * calculatePasswordStrength('Password123!')
 * // Returns: { strength: 4, label: 'Strong', color: 'green', feedback: 'Excellent password!' }
 */
export const calculatePasswordStrength = (password) => {
  if (!password) {
    return {
      strength: 0,
      label: 'None',
      color: 'gray',
      feedback: 'Enter a password'
    };
  }

  let strength = 0;
  const feedback = [];

  // Check length
  if (password.length >= 8) {
    strength++;
  } else {
    feedback.push('at least 8 characters');
  }

  // Check for uppercase
  if (/[A-Z]/.test(password)) {
    strength++;
  } else {
    feedback.push('uppercase letter');
  }

  // Check for lowercase
  if (/[a-z]/.test(password)) {
    strength++;
  } else {
    feedback.push('lowercase letter');
  }

  // Check for numbers
  if (/\d/.test(password)) {
    strength++;
  } else {
    feedback.push('number');
  }

  // Check for special characters
  if (/[^A-Za-z0-9]/.test(password)) {
    strength++;
  } else {
    feedback.push('special character');
  }

  // Determine label and color
  let label, color, message;

  if (strength <= 1) {
    label = 'Weak';
    color = 'red';
    message = feedback.length > 0
      ? `Add ${feedback.slice(0, 2).join(', ')}${feedback.length > 2 ? '...' : ''}`
      : 'Password is too weak';
  } else if (strength === 2) {
    label = 'Fair';
    color = 'orange';
    message = feedback.length > 0
      ? `Add ${feedback.join(', ')}`
      : 'Password is fair';
  } else if (strength === 3) {
    label = 'Good';
    color = 'yellow';
    message = feedback.length > 0
      ? `Add ${feedback.join(', ')} for stronger security`
      : 'Good password!';
  } else if (strength === 4) {
    label = 'Strong';
    color = 'green';
    message = 'Excellent password!';
  } else {
    label = 'Very Strong';
    color = 'green';
    message = 'Excellent password with all requirements met!';
  }

  return {
    strength,
    label,
    color,
    feedback: message
  };
};

/**
 * Validate email format using enterprise-grade validation
 *
 * Uses @outdoorguides/shared validation which includes:
 * - RFC 5322 format validation
 * - Disposable email blocking
 * - Role-based email blocking
 * - Context-specific rules
 *
 * @param {string} email - Email address to validate
 * @param {Object} options - Validation options (context, requireBusinessEmail, etc.)
 * @returns {boolean} True if valid email format
 *
 * @example
 * isValidEmail('user@example.com') // true
 * isValidEmail('user@tempmail.com') // false (disposable)
 * isValidEmail('admin@example.com') // false (role-based)
 */
export const isValidEmail = (email, _options = {}) => {
  // Basic email validation regex
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

  if (!email || typeof email !== 'string') {
    return false;
  }

  return emailRegex.test(email.trim());
};

/**
 * Validate email with detailed error messages
 * Use this for form validation to show specific errors to users
 *
 * @param {string} email - Email address to validate
 * @param {Object} options - Validation options
 * @returns {Object} { valid: boolean, errors: Array, warnings: Array }
 *
 * @example
 * const result = validateEmailDetailed('user@tempmail.com');
 * if (!result.valid) {
 *   console.log(result.errors[0].message); // "Disposable email addresses are not allowed"
 * }
 */
export const validateEmailDetailed = (email, _options = {}) => {
  // Basic email validation with detailed response
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

  if (!email || typeof email !== 'string') {
    return {
      valid: false,
      errors: [{ field: 'email', message: 'Email address is required' }],
      warnings: []
    };
  }

  const trimmedEmail = email.trim();

  if (!emailRegex.test(trimmedEmail)) {
    return {
      valid: false,
      errors: [{ field: 'email', message: 'Invalid email format' }],
      warnings: []
    };
  }

  return {
    valid: true,
    errors: [],
    warnings: []
  };
};

/**
 * Validate phone number (US format)
 * @param {string} phone - Phone number to validate
 * @returns {boolean} True if valid 10-digit US phone number
 */
export const isValidPhone = (phone) => {
  // Strip all non-digits
  let cleaned = phone.replace(/\D/g, '');

  // Strip leading 1 (US country code) if present and there are 11 digits
  if (cleaned.length === 11 && cleaned[0] === '1') {
    cleaned = cleaned.substring(1);
  }

  // Must be exactly 10 digits
  return cleaned.length === 10;
};

/**
 * Strip formatting from phone number
 * @param {string} formatted - Formatted phone number
 * @returns {string} Digits only
 */
export const stripPhoneFormatting = (formatted) => {
  return formatted.replace(/\D/g, '');
};

/**
 * Validate day rate range
 * @param {number} cents - Day rate in cents
 * @returns {boolean} True if within valid range ($100-$2000)
 */
export const isValidDayRate = (cents) => {
  return cents >= 10000 && cents <= 200000;
};

/**
 * Format cents to dollar string
 * @param {number} cents - Amount in cents
 * @returns {string} Formatted dollar amount
 * @example formatCentsToDollars(10000) // Returns: '$100.00'
 */
export const formatCentsToDollars = (cents) => {
  // Handle null/undefined/NaN/Infinity by treating as 0
  if (!Number.isFinite(cents)) {
    return '$0.00';
  }
  const dollars = cents / 100;
  const isNegative = dollars < 0;
  const absoluteDollars = Math.abs(dollars);
  const formatted = absoluteDollars.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  return isNegative ? `-$${formatted}` : `$${formatted}`;
};

/**
 * Parse dollar string to cents.
 * Strips currency symbols and commas before parsing.
 * SCRUM-1296: Hardened with null/undefined/NaN guards.
 * @param {string|null|undefined} dollarString - Dollar amount as string
 * @returns {number} Amount in cents, 0 for null/unparseable
 * @example parseDollarsToCents('$100.00') // Returns: 10000
 */
export const parseDollarsToCents = (dollarString) => {
  if (dollarString === null || dollarString === undefined) { return 0; }
  const cleaned = String(dollarString).replace(/[$,]/g, '');
  if (cleaned === '') { return 0; }
  const dollars = parseFloat(cleaned);
  if (!Number.isFinite(dollars)) { return 0; }
  return Math.round(dollars * 100);
};

/**
 * Convert integer cents to a float dollar value.
 * SCRUM-1296: Canonical cents→dollars numeric conversion.
 * @param {number|null|undefined} cents
 * @returns {number} e.g. 1234.56, 0 for null/NaN
 */
export const centsToDollarsFloat = (cents) => {
  if (cents === null || cents === undefined || !Number.isFinite(Number(cents))) {
    return 0;
  }
  return Number(cents) / 100;
};

/**
 * Convert a dollar number to integer cents.
 * SCRUM-1296: Canonical dollars→cents numeric conversion.
 * @param {number|null|undefined} dollars
 * @returns {number} Integer cents, 0 for null/NaN
 */
export const dollarsToCents = (dollars) => {
  if (dollars === null || dollars === undefined || !Number.isFinite(Number(dollars))) {
    return 0;
  }
  return Math.round(Number(dollars) * 100);
};

/**
 * Calculate total booking price
 * Implements MVP 1.4 pricing logic (application layer calculation)
 * @param {object} booking - Booking object with base_price_cents, additional_fees_cents, discount_cents
 * @returns {number} Total price in cents
 * @example calculateTotalCents({ base_price_cents: 89900, additional_fees_cents: 0, discount_cents: 0 }) // Returns: 89900
 */
export const calculateTotalCents = (booking) => {
  return (booking.base_price_cents || 0) +
         (booking.additional_fees_cents || 0) -
         (booking.discount_cents || 0);
};

/**
 * Calculate final payment amount (after deposit)
 * @param {object} booking - Booking object with pricing and deposit_amount_cents
 * @returns {number} Final payment in cents
 * @example calculateFinalPaymentCents({ base_price_cents: 89900, deposit_amount_cents: 26970 }) // Returns: 62930
 */
export const calculateFinalPaymentCents = (booking) => {
  return calculateTotalCents(booking) - (booking.deposit_amount_cents || 0);
};

/**
 * Calculate platform fee using the provided fee rate.
 * @param {number} totalCents - Total price in cents
 * @param {number} feeRate - Fee rate as decimal (e.g., 0.08 for 8%). Required — no default.
 * @returns {number} Platform fee in cents
 * @example calculatePlatformFeeCents(89900, 0.08) // Returns: 7192
 */
export const calculatePlatformFeeCents = (totalCents, feeRate) => {
  if (feeRate === undefined || feeRate === null) {
    throw new Error('feeRate is required for platform fee calculation');
  }
  return Math.round(totalCents * feeRate);
};

/**
 * Calculate deposit amount (30% for trips >= $1000)
 * @param {number} totalCents - Total price in cents
 * @returns {number} Deposit amount in cents (0 if under $1000, 30% if $1000+)
 * @example calculateDepositCents(89900) // Returns: 0 (under $1000)
 * @example calculateDepositCents(120000) // Returns: 36000 (30% of $1200)
 */
export const calculateDepositCents = (totalCents) => {
  if (totalCents < 100000) { // Less than $1000
    return 0; // Full payment required
  }
  return Math.round(totalCents * 0.30); // 30% deposit
};

/**
 * Format timer display (MM:SS)
 * @param {number} seconds - Seconds remaining
 * @returns {string} Formatted time string
 * @example formatTimer(125) // Returns: "2:05"
 * @example formatTimer(59) // Returns: "0:59"
 */
export const formatTimer = (seconds) => {
  const mins = Math.floor(seconds / 60);
  const secs = seconds % 60;
  return `${mins}:${secs.toString().padStart(2, '0')}`;
};

/**
 * Generate confirmation number
 * Format: ABC-12345 (3 letters + dash + 5 digits)
 * @returns {string} Confirmation number
 * @example generateConfirmationNumber() // Returns: "DK7-28491"
 */
export const generateConfirmationNumber = () => {
  // Standard character sets for confirmation codes (not secrets)
  const letters = 'ABCDEFGHJKLMNPQRSTUVWXYZ'; // eslint-disable-line no-secrets/no-secrets
  const digits = '23456789';
  let result = '';

  // 3 random letters
  for (let i = 0; i < 3; i++) {
    result += letters.charAt(Math.floor(Math.random() * letters.length));
  }

  result += '-';

  // 5 random digits (excluding 0 and 1)
  for (let i = 0; i < 5; i++) {
    result += digits.charAt(Math.floor(Math.random() * digits.length));
  }

  return result;
};

/**
 * Calculate final payment due date based on cancellation policy
 * @param {Date|string} tripDate - Trip start date
 * @param {number} fullRefundDays - Days before trip for full refund (from cancellation policy)
 * @returns {Date} Final payment due date
 * @example calculateFinalPaymentDueDate('2025-03-15', 7) // Returns: Date 14 days before trip
 */
export const calculateFinalPaymentDueDate = (tripDate, fullRefundDays = 7) => {
  const trip = new Date(tripDate);
  const dueDate = new Date(trip);

  // Final payment due 7 days before the cancellation cutoff
  // (e.g., if full refund until 7 days before, final payment due 14 days before)
  dueDate.setDate(trip.getDate() - (fullRefundDays + 7));

  return dueDate;
};

/**
 * Format date for display (Month Day, Year)
 * @param {Date|string} date - Date to format
 * @returns {string} Formatted date string
 * @example formatDisplayDate('2025-03-15') // Returns: "March 15, 2025"
 */
export const formatDisplayDate = (date) => {
  const d = new Date(date);
  // Use UTC to avoid timezone conversion issues
  return d.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    timeZone: 'UTC'
  });
};

/**
 * Strip phone formatting to E.164 format (+1XXXXXXXXXX)
 * @param {string} formatted - Formatted phone number
 * @returns {string} E.164 format phone number
 * @example stripPhoneToE164('(555) 123-4567') // Returns: '+15551234567'
 */
export const stripPhoneToE164 = (formatted) => {
  const digits = formatted.replace(/\D/g, '');

  // Add +1 country code if not present
  if (digits.length === 10) {
    return `+1${digits}`;
  } else if (digits.length === 11 && digits[0] === '1') {
    return `+${digits}`;
  }

  return `+${digits}`;
};
